// Order of cards in a board column and what a drop writes: the manual place (rank) or, when the view is sorted, the sort's place.

import { rankBetween, ranksBetween, type Id } from '@confdeck/core-shared';

type RankOf = (id: Id) => string | null;

/**
 * Cards of a column in manual order: cards without a place first, in the query's order (a new card lands at the top),
 * then the placed ones by rank.
 */
export function manualOrder(ids: Id[], rank: RankOf): Id[] {
  const loose: Id[] = [];
  const placed: { id: Id; r: string }[] = [];
  for (const id of ids) { const r = rank(id); if (r) placed.push({ id, r }); else loose.push(id); }
  placed.sort((a, b) => (a.r < b.r ? -1 : a.r > b.r ? 1 : 0));
  return loose.length ? [...loose, ...placed.map((x) => x.id)] : placed.map((x) => x.id);
}

/** At most this many cards without a place get one in a single drop (the server takes as many). */
export const MAX_RANKS = 200;

/**
 * What a drop at `index` of `list` (the column in manual order, without the moved card) writes: the card's rank and, when it
 * lands among cards without a place, places for those cards in their current order. Cards after them keep theirs.
 */
export function placeAt(list: Id[], index: number, rank: RankOf): { rank: string; ranks: { id: Id; rank: string }[] } {
  let loose = 0;
  while (loose < list.length && !rank(list[loose]!)) loose++;
  const ranks: { id: Id; rank: string }[] = [];
  const own = new Map<Id, string>();
  // Dropped after the loose cards: no neighbour needs a place, the card goes before the first placed one.
  if (index < loose) {
    const n = Math.min(loose, MAX_RANKS);
    const rs = ranksBetween(null, loose === n && loose < list.length ? rank(list[loose]!) : null, n);
    list.slice(0, n).forEach((id, i) => { own.set(id, rs[i]!); ranks.push({ id, rank: rs[i]! }); });
  }
  const at = (i: number) => (i < 0 || i >= list.length ? null : own.get(list[i]!) ?? rank(list[i]!));
  return { rank: rankBetween(index >= loose || own.size ? at(index - 1) : null, at(index)), ranks };
}

/**
 * The place a card takes in a sorted column: the sort does not change when the stage does, so the place follows the
 * card's position in the whole sorted result.
 */
export function sortedIndex(columnIds: Id[], moved: Id, position: (id: Id) => number): number {
  const p = position(moved);
  let n = 0;
  for (const id of columnIds) if (id !== moved && position(id) < p) n++;
  return n;
}
